import { cleanSmartQuotesAndPunctuation, normalizeLetterCase } from '../core/typography';
import type { CaseMode } from '../core/types';
import { getEditorValue, setEditorValue } from './editor';
import { triggerHaptic } from './haptics';
import { setHistorySnapshot, showToast } from './toast';
import { updateCounters } from './app';

/**
 * Closes the format tools dropdown menu.
 */
export function closeFormatMenu(): void {
  const menu = document.getElementById('formatMenu');
  const toggleBtn = document.getElementById('formatToggleBtn');
  if (menu && toggleBtn) {
    menu.classList.remove('show');
    toggleBtn.setAttribute('aria-expanded', 'false');
  }
}

/**
 * Executes a formatting transformation on the editor with undo safeguarding.
 */
function applyTransformation(
  transformFn: (text: string) => { text: string; label: string }
): void {
  const rawText = getEditorValue();

  if (!rawText.trim()) {
    showToast('No lyrics in editor to format.');
    return;
  }

  // Save history snapshot for Undo support
  setHistorySnapshot({
    lyrics: rawText,
    input: rawText
  });

  const { text: transformedText, label } = transformFn(rawText);
  setEditorValue(transformedText);

  updateCounters();
  triggerHaptic(12);
  closeFormatMenu();

  showToast(`✓ ${label}! • <span class="undo-action">Undo</span>`);
}

/**
 * Normalizes quotes, dashes, and unicode punctuation on the active editor text.
 */
export function handleCleanPunctuation(): void {
  applyTransformation((text) => {
    const { text: cleaned, changesCount } = cleanSmartQuotesAndPunctuation(text);
    return {
      text: cleaned,
      label: `Cleaned ${changesCount} smart quotes & dashes`
    };
  });
}

/**
 * Normalizes lyric letter case on the active editor text.
 */
export function handleCaseNormalization(mode: CaseMode): void {
  const modeLabels: Record<CaseMode, string> = {
    sentence: 'Converted to Sentence case',
    title: 'Converted to Title Case',
    lower: 'Converted to lowercase',
    upper: 'Converted to UPPERCASE'
  };

  applyTransformation((text) => ({
    text: normalizeLetterCase(text, mode),
    label: modeLabels[mode]
  }));
}

/**
 * Initializes the Format Tools dropdown and actions.
 */
export function initFormatMenu(): void {
  const toggleBtn = document.getElementById('formatToggleBtn');
  const menu = document.getElementById('formatMenu');
  const dropdown = document.getElementById('formatDropdown');

  const cleanPunctuationBtn = document.getElementById('cleanPunctuationBtn');
  const caseSentenceBtn = document.getElementById('caseSentenceBtn');
  const caseTitleBtn = document.getElementById('caseTitleBtn');
  const caseLowerBtn = document.getElementById('caseLowerBtn');
  const caseUpperBtn = document.getElementById('caseUpperBtn');

  // Toggle dropdown
  if (toggleBtn && menu) {
    toggleBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = menu.classList.contains('show');
      if (isOpen) {
        closeFormatMenu();
      } else {
        menu.classList.add('show');
        toggleBtn.setAttribute('aria-expanded', 'true');
        triggerHaptic(8);
      }
    });

    document.addEventListener('click', (e) => {
      if (dropdown && !dropdown.contains(e.target as Node)) {
        closeFormatMenu();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeFormatMenu();
      }
    });
  }

  // Action clicks
  if (cleanPunctuationBtn) cleanPunctuationBtn.addEventListener('click', handleCleanPunctuation);
  if (caseSentenceBtn) caseSentenceBtn.addEventListener('click', () => handleCaseNormalization('sentence'));
  if (caseTitleBtn) caseTitleBtn.addEventListener('click', () => handleCaseNormalization('title'));
  if (caseLowerBtn) caseLowerBtn.addEventListener('click', () => handleCaseNormalization('lower'));
  if (caseUpperBtn) caseUpperBtn.addEventListener('click', () => handleCaseNormalization('upper'));
}
